<template>
  <div class="grid-panel">
    <grid-action-bar
         @update:search="search = $event"
         @update:deleted="onUpdatedDeleted"
        :show-deleted="showDeletedToggle"
        :show-search="showSearch"
        :title="title"
    >
      <slot slot="top-right" name="top-right"></slot>
      <slot slot="bottom-left" name="bottom-left"></slot>
      <slot slot="bottom-right" name="bottom-right"></slot>
      <slot></slot>

    </grid-action-bar>

    <t-grid
        ref="grid"
        :class="height"
        :auto-size="autoSize"
        :column-defs="columnDefs"
        :auto-group-column-def="autoGroupColumnDef"
        :data="internalData"
        :loading="busy"
        :gridOptions="gridOptions"
        :search="search"
        @rowClicked="$emit('rowClicked', $event)"
        @cellClicked="$emit('cellClicked', $event)"
        @download="$emit('download', $event)"
        @rowSelected="$emit('rowSelected', $event)"
        @update:gridApi="$emit('update:gridApi', $event)"
        :frameworkComponents="frameworkComponents"
        :rowSelection="rowSelection"
    />
  </div>
</template>

<script>
import GridActionBar from '@/views/components/extra-components/GridActionBar';
import TGrid from '@/views/components/ag-grid/TGrid';
import api from '@/api';
import NotificationMixin from '@/components/mixins/NotificationMixin';
import CellRendererButton from "@/views/components/ag-grid/cell-renderer/CellRendererButton";

export default {
  name: 'Grid-Panel',
  components: {
    GridActionBar,
    TGrid,
  },
  mixins: [NotificationMixin],
  props: {
    autoSize: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
    },
    url: {
      type: [String, Function, Promise],
      required: false,
    },
    columnDefs: {
      type: Array,
      required: true,
    },
    autoGroupColumnDef: {
      type: Object,
      required: false,
    },
    gridOptions: {
      type: Object,
      default: () => ({
        // rowHeight: 45 TODO: Change row height
      }),
    },
    data: {
      type: [Array, null],
      required: false,
      default: () => null,
    },
    apiParams: {
      type: Object,
      default: () => {
      },
    },
    loadOnRender: {
      type: Boolean,
      default: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    searchQuery: {
      required: false,
      default: '',
    },
    showDeletedToggle: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    rowSelection: {
      type: String,
      default: "multiple",
    },
  },
  data() {
    return {
      internalLoading: false,
      internalData: [],
      search: '',
      frameworkComponents: {
        buttonCellRenderer: CellRendererButton,
      },
      gridApi: null,
    };
  },
  computed: {
    busy() {
      return this.internalLoading || this.loading;
    }
  },
  watch: {
    /**
     * Override data
    */
    data: {
      handler(newData) {
        this.internalData = newData;
      },
      immediate: true,
    },
    /**
     * Refresh data when parameters change
     */
    apiParams: {
      handler(newParams) {
        // We may want to add a *debounce* function here
        // If we change the apiParams too much we
        // don't want to trigger the update immediately
        this.list(newParams);
      },
      deep: true,
    },

    searchQuery(newData) {
      this.search = newData;
    }
  },
  mounted() {
    if (this.loadOnRender) {
      this.list(this.apiParams);
    }
  },
  methods: {
    /**
     * Get data
     *
     * @param params
     * @returns {Promise<void>}
     */
    async list(params = {}) {
      if (!this.url) {
        return;
      }

      try {
        this.internalLoading = true;
        let data = [];

        // Prop could be something like this.$api.products.list;
        if (typeof this.url === 'function') {
          const response = await this.url({params});
          
          if (response.data) {
            data = response.data.data;
          }
        } else {
          const response = await api.list(this.url, { params });
          data = response.data.data;
        }

        // If we do have data being passed in, we don't want to set it
        if (!this.data) {
          this.internalData = data;
        }

        // Emit that we have finished fetching
        this.$emit('fetched', data);
      } catch (e) {
        console.log(e);
        this.notifyError('Unable to load dataset');
      } finally {
        this.internalLoading = false;
      }
    },
    onUpdatedDeleted(isDeleted) {
      this.$emit('update:deleted', isDeleted);
      if (!this.data) {
        this.list({IsDeleted: isDeleted})
      }
    }
  }

};
</script>

<style lang="scss">
  .ag-theme-material {
    font-family: Arial, Helvetica, sans-serif;
    
    .ag-header-row {
      color: #262626;
    }
    .ag-checkbox-input-wrapper.ag-checked::after {
      color: #110D0D;
    }
  }

</style>
